import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, OnDestroy, OnInit, Output, OnChanges, SimpleChanges, inject, input, output } from '@angular/core';
import { Router } from '@angular/router';
import { InterventionTask } from 'src/app/core/models/task.model';

import { InterventionTaskService } from 'src/app/core/services/intervention-task.service';
import { ApplicationStateService } from 'src/app/core/services/application-state.service';
import { StudentDataService } from 'src/app/core/services/student-data.service';
import { AudioPlayerService } from 'src/app/core/services/audio-player.service';

import { TotalPointsComponent } from '../total-points/total-points.component';
import { InterventionBackDialogComponent } from '../../shared/components/intervention-back-dialog/intervention-back-dialog.component';

@Component({
    selector: 'task-bar',
    templateUrl: './task-bar.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [TotalPointsComponent, InterventionBackDialogComponent]
})
export class TaskBarComponent implements OnInit, OnDestroy, OnChanges {
  readonly totalPoints = input<number>();
  readonly indicatorTimer = input<number>(); // optional indicator timer input

  readonly taskAnimationComplete = output<boolean>();

  private readonly interventionTaskService = inject(InterventionTaskService);
  private readonly studentDataService = inject(StudentDataService);
  private readonly applicationStateService = inject(ApplicationStateService);
  private readonly audioService = inject(AudioPlayerService);
  private readonly router = inject(Router);
  private readonly changeDetector = inject(ChangeDetectorRef);

  // TEMP for initial compilation
  taskName: string = '';
  taskSubtext: string = '';
  taskIcon: string = '';
  showBackDialog: boolean = false;
  backButtonVisible: boolean = false;
  taskBarTaskDetailsVisible: boolean = false;
  animationDelay: number = 3000;
  waitForAnimationTimer: number | null = null;
  backButtonTimeout: number | null = null;
  animate: boolean = true;

  animationComplete: boolean = false;
  title: string;
  taskBarColor: string;
  disableBackButton: boolean = false;
  parentTaskId: string = '' ;
  isPulse: boolean = false; // control variable for pulsing effect

  constructor() {
    const studentDataService = this.studentDataService;

    this.title = studentDataService.getTaskBarTitleBasedOnCurriculumPlacement();
    this.taskBarColor = studentDataService.getTaskBarColor();
    let navigationExtras = this.router.currentNavigation()?.extras.state;
    this.parentTaskId = navigationExtras?.parentTaskId || '' ;
    this.animate = navigationExtras?.animate ?? true ;
  }

  ngOnInit(): void {
    let destinationValue = this.applicationStateService.getCurrentDestination();
    let selectedTask = this.studentDataService.getSelectedTask();

    if (this.studentDataService.areAllTasksCompleteForDestination(destinationValue) || (selectedTask !== null && this.studentDataService.isInterventionUnit())) {
      this.backButtonVisible = true;
    }else{
      this.backButtonVisible = false;
    }

    this.taskBarTaskDetailsVisible = (selectedTask != null);

    this.disableBackButton = true;
    this.backButtonTimeout = window.setTimeout(() => {
      this.disableBackButton = false;
      this.changeDetector.markForCheck() ;
    }, 4300);

    if (selectedTask !== null) {
      this.taskIcon = "/assets/images/destination/" + selectedTask.taskIcon + ".svg";
      this.taskSubtext = selectedTask.taskSubtext ;

      let taskName = selectedTask.taskType.replace('...',' ') + selectedTask.taskName;
      taskName = taskName.toUpperCase().replace('PASSAGES ','');
      this.taskName = taskName;
    }

    if (selectedTask === null || !this.animate) {
      //No animation occurs when we aren't in a task
      this.animationDelay = 0;
      this.backButtonVisible = true;
    }

    this.waitForAnimationTimer = window.setTimeout(() => {
      this.taskAnimationComplete.emit(true);
      this.animationComplete = true;
      this.changeDetector.markForCheck();
      this.startPulseTimer() ;
    }, this.animationDelay);
  }

  ngOnChanges(changes: SimpleChanges) {
    // Check if `indicatorTimer` changes
    if (changes.indicatorTimer) {
      this.startPulseTimer();
    }
  }

  startPulseTimer() {
    // Clear any existing timeout
    const indicatorTimer = this.indicatorTimer();
    if (indicatorTimer) {
      clearTimeout(indicatorTimer);
    }
    // Start a new timeout if `indicatorTimer` is defined
    if (indicatorTimer) {
      setTimeout(() => {
        this.isPulse = true;
        this.changeDetector.markForCheck();
      }, indicatorTimer);
    }
  }

  ngOnDestroy(): void {
    if (this.waitForAnimationTimer !== null) {
      window.clearTimeout(this.waitForAnimationTimer);
    }
    if (this.backButtonTimeout !== null) {
      window.clearTimeout(this.backButtonTimeout);
    }
    const indicatorTimer = this.indicatorTimer();
    if (indicatorTimer !== undefined) {
      window.clearTimeout(indicatorTimer);
    }
  }

  onBackDialogPress(response: boolean) {
    this.showBackDialog = false ;

    if (response) {
      this.goBack() ;
    }
    else
    {
      this.audioService.stopAll() ;
    }
  }

  onBackButtonPress() {
    const trialCompleted = this.interventionTaskService.consecutiveCorrect > 0 || this.interventionTaskService.consecutiveIncorrect > 0 ;
    if (!trialCompleted || this.interventionTaskService.task.passage)
    {
      this.goBack() ;
    }
    else
    {
      this.audioService.stopAll() ;
      this.audioService.play('Audio/Help/transition_leavetask.mp3').subscribe() ;
      this.showBackDialog = true ;
    }
  }

  goBack(): void {
    // Firs thing to do when navigating away, stop any audio that is playing
    this.audioService.stopAll() ;

    // Returning from a task page
    if (this.parentTaskId) {
      this.router.navigateByUrl('/subtaskSelect', {
        state: {
          canGoToTaskSelect: true,
          previousTaskScoreData: this.interventionTaskService.getPreviousTaskScoreData(),
          mainTaskId: this.parentTaskId,
        }
      });
    }
    else {
      this.router.navigateByUrl('/taskSelect',
        {state: {canGoToTaskSelect: true, previousTaskScoreData: this.interventionTaskService.getPreviousTaskScoreData()}});
    }
  }
}
